import { ClassValue } from 'clsx';
import { useCallback } from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';

import { Icon } from '@/components/icons';
import { IconName } from '@/components/icons/types';
import { cn } from '@/utils/styles';

import { Typography } from '../typography';

import { colors, sizes } from './constants';
import { InputColor, InputSize, InputType } from './types';

export type InputProps = {
  readonly id?: string;
  readonly type?: InputType;
  readonly size?: InputSize;
  readonly color?: InputColor;
  readonly step?: string;
  readonly min?: number | string;
  readonly max?: number | string;
  readonly prefix?: string;
  readonly suffix?: string;
  readonly leftIcon?: IconName;
  readonly rightIcon?: IconName;
  readonly placeholder?: string;
  readonly autoComplete?: string;
  readonly defaultValue?: string;
  readonly registration?: Partial<UseFormRegisterReturn>;
  readonly className?: ClassValue;
  readonly accept?: string;
  readonly readOnly?: boolean;
  readonly mask?: (value: string | null) => string;
  readonly rightElement?: React.ReactNode;
  readonly onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  readonly maxLength?: number;
};

export function Input({
  id,
  type = 'text',
  size = 'md',
  color = 'primary',
  step = '0.01',
  min = 0,
  max,
  prefix,
  suffix,
  leftIcon,
  rightIcon,
  placeholder,
  autoComplete = 'off',
  defaultValue,
  registration,
  className,
  readOnly = false,
  mask,
  rightElement,
  onFocus,
  maxLength,
}: InputProps) {
  const isDisabled = registration?.disabled ?? false;
  const isLarge = size === 'lg';
  const isNumber = type === 'number';

  const handleOnKeyUp = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (mask) {
        event.preventDefault();
        event.currentTarget.value = mask(event.currentTarget.value);
      }
    },
    [mask]
  );

  const handleOnWheel = useCallback((event: React.WheelEvent<HTMLInputElement>) => {
    event.currentTarget.blur();
  }, []);

  // Blocks '-', '+' and exponential notation from keyboard, paste and drag & drop.
  const handleOnBeforeInput = useCallback((event: React.FormEvent<HTMLInputElement>) => {
    const { data } = event.nativeEvent as InputEvent;

    if (data && /[-+eE]/.test(data)) {
      event.preventDefault();
    }
  }, []);

  return (
    <div className="group flex items-stretch justify-start">
      {prefix ? (
        <span
          className={cn(
            'bg-neutral-25 rounded-l border-y border-l border-neutral-50',
            isDisabled ? 'cursor-not-allowed' : 'cursor-default',
            'flex shrink-0 items-center justify-center',
            sizes[size]
          )}
        >
          <Typography size={isLarge ? 'body-1' : 'body-2'} color="secondary" weight="normal">
            {prefix}
          </Typography>
        </span>
      ) : null}
      <div className="relative w-full">
        {leftIcon && (
          <span
            className={cn(
              'absolute top-px left-px z-2',
              'flex w-10 items-center justify-center',
              isDisabled ? 'cursor-not-allowed' : 'cursor-default',
              isLarge ? 'h-11' : 'h-10',
              'pointer-events-none'
            )}
          >
            <Icon name={leftIcon} className={cn('text-neutral-500', isLarge ? 'h-6 w-6' : 'h-5 w-5')} />
          </span>
        )}

        <input
          id={id}
          type={type}
          {...(isNumber ? { step, min, max } : {})}
          placeholder={placeholder}
          autoComplete={autoComplete}
          defaultValue={defaultValue}
          disabled={isDisabled}
          readOnly={readOnly}
          className={cn(
            'w-full',
            'placeholder:text-base-secondary text-base-primary font-normal sm:text-base sm:leading-6',
            sizes[size],
            'appearance-none border outline-none',
            // hides arrows in number inputs
            '[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
            '[-moz-appearance:textfield] [&::-webkit-inner-spin-button]:m-0',
            'focus:outline-none',
            'focus:ring-2 focus:ring-offset-0',
            colors[color],
            'disabled:bg-neutral-25 disabled:border-neutral-50',
            'disabled:text-neutral-disabled disabled:placeholder:text-neutral-disabled',
            'disabled:cursor-not-allowed',
            { 'pl-10': leftIcon, 'pr-10': rightIcon },
            { 'rounded-l': !prefix, 'rounded-r': !suffix },
            className
          )}
          onKeyUp={handleOnKeyUp}
          {...registration}
          {...(isNumber ? { onWheel: handleOnWheel, onBeforeInput: handleOnBeforeInput } : {})}
          onFocus={onFocus}
          maxLength={maxLength}
        />

        {maxLength && (
          <Typography as="p" size="body-2" weight="normal" className="italic" color="secondary">
            {`Max ${maxLength} characters`}
          </Typography>
        )}

        {rightIcon && (
          <span
            className={cn(
              'absolute top-px right-px z-2',
              'flex w-10 items-center justify-center',
              isDisabled ? 'cursor-not-allowed' : 'cursor-default',
              isLarge ? 'h-11' : 'h-10',
              'pointer-events-none'
            )}
          >
            <Icon name={rightIcon} className={cn('text-neutral-500', isLarge ? 'h-6 w-6' : 'h-5 w-5')} />
          </span>
        )}
        {rightElement}
      </div>
      {suffix ? (
        <span
          className={cn(
            'bg-neutral-25 rounded-r border-y border-r border-neutral-50',
            isDisabled ? 'cursor-not-allowed' : 'cursor-default',
            'flex shrink-0 items-center justify-center',
            sizes[size]
          )}
        >
          <Typography size={isLarge ? 'body-1' : 'body-2'} color="secondary" weight="normal">
            {suffix}
          </Typography>
        </span>
      ) : null}
    </div>
  );
}
